// Contract quota maths for the KJP summary (R01 Pavement, R02 Road Shoulder,
// R06 Road Line Marking).
//
// Pure by design: no Vue, no API, no DOM. Every quota, ceiling, baki and
// percentage in the KJP tables is derived here and nowhere else, so the screen
// and the Excel export cannot disagree. Kept dependency-free so it can move
// server-side unchanged if the CMS ever needs the same figures.
//
// Formulas reverse-engineered from the 2026 workbook and verified against all
// nine districts — see scripts/verify-kjp.mjs.

export const SCOPES = ['R01', 'R02', 'R06']

// Round to 2dp the way the workbook displays, not the way JS does.
//
// 60410.70 / 12 is 5034.225 in decimal but 5034.224999999999 in binary, so
// toFixed(2) yields 5034.22 while the sheet shows 5034.23. Snapping to 12
// significant digits first discards the representation error, then we round
// half away from zero. Every quota in the workbook reproduces exactly under
// this; plain toFixed does not.
export function round2(n) {
  const snapped = Number(Number(n).toPrecision(12))

  return (Math.sign(snapped) * Math.round(Math.abs(snapped) * 100)) / 100
}

// Contract terms, not physics. Identical across all nine districts for 2026;
// revisit at contract renewal, when a backend collection may be worth it.
//
// areaFactor 7 is the assumed carriageway width in metres.
// The workbook labels R01's Heavy Patching row "20%" and Cold Asphalt "30%",
// but 60/10/30 is what reproduces every cell in every district. The labels are
// wrong, not the arithmetic.
export const CONTRACT_TERMS = {
  R01: {
    areaFactor: 7,
    quotaPct: 0.01,
    split: { hotmix: 0.6, heavy_patching: 0.1, coldmix: 0.3 },
  },
  R02: { protocolPct: 0.3, secondaryPct: 0.1 },
  R06: { areaFactor: 7, protocolPct: 0.007, secondaryPct: 0.003 },
}

// Contract lengths are stored per road class in km. Only the two pair sums are
// ever used, so how a pair divides internally does not matter here.
export function districtLengths(district) {
  const km = (v) => (v == null || v === '' ? 0 : Number(v))
  const d = district || {}

  return {
    pu: (km(d.protocolKM) + km(d.primaryKM)) * 1000,
    sm: (km(d.secondaryKM) + km(d.minorKM)) * 1000,
  }
}

// The endpoint declares its unit per response: "m" for R02 (shoulder length),
// "m2" for R01 and R06 (patched / marked area). R02 is contracted and reported
// in km, so metres are scaled down; areas pass through untouched.
//
// Driven by the response's `unit`, never by reportType — the backend owns that
// mapping and says so explicitly in its contract. Declared here, ahead of
// annualQuota, because it is now that function's single source for R02's
// scaling too (see the comment there) — bucketUsage below reads the exact
// same object, so quota and usage cannot independently drift out of unit.
const UNIT_SCALE = { m: 0.001, m2: 1 }

// Annual quota in m² (R01, R06) or km (R02).
//
// R02's contract-length quantity (pu * protocolPct + sm * secondaryPct) is a
// metre quantity, built from the same metre-denominated pu/sm districtLengths
// returns — the same kind of quantity bucketUsage scales down for R02 usage
// (unit "m"). Scaling it here with that same UNIT_SCALE.m factor, rather than
// a second constant of its own, is what keeps quota and usage from ever
// disagreeing about units again: there is exactly one 0.001 in this module.
export function annualQuota(scope, lengths) {
  const { pu, sm } = lengths
  const t = CONTRACT_TERMS[scope]

  if (scope === 'R01') return (pu + sm) * t.areaFactor * t.quotaPct
  if (scope === 'R02') return (pu * t.protocolPct + sm * t.secondaryPct) * UNIT_SCALE.m

  return pu * t.areaFactor * t.protocolPct + sm * t.areaFactor * t.secondaryPct
}

// R01 splits its quota across three work types. R02/R06 do not split.
export function quotaSplit(scope, annual) {
  const t = CONTRACT_TERMS[scope]
  if (!t.split) return null

  return Object.keys(t.split).reduce((acc, key) => {
    acc[key] = annual * t.split[key]

    return acc
  }, {})
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// The contract period, not the calendar year, sets the divisor. A Mar-Dec
// contract divides the annual quota by 10, which is the entire difference
// between the workbook's _12m and _10m sheets.
export function monthlyCeiling(annual, monthCount) {
  if (!monthCount) return 0

  return annual / monthCount
}

// The table's month columns. Always the twelve calendar months: the workbook's
// _10m sheet still runs Jan to Dec, and its annual roll-up sums all twelve even
// though the contract starts in March. Only the divisor and the cumulative
// window respond to the contract period.
export function calendarMonths(year) {
  const yy = String(year).slice(-2)

  return MONTH_ABBR.map((abbr, i) => ({ index: i + 1, label: `${abbr}-${yy}` }))
}

// Contract months elapsed at the as-at month, clamped to the period.
export function elapsedMonths(startMonth, asAtMonth, monthCount) {
  return Math.max(0, Math.min(asAtMonth - startMonth + 1, monthCount))
}

// Sub-rows per scope, in workbook order. `key` maps to an endpoint group;
// null means the row is derived rather than grouped.
export const USAGE_ROWS = {
  R01: [
    { key: null,             label: 'Bilangan Potholes',      kind: 'count' },
    { key: 'hotmix',         label: 'Pothole (1x1m)',         kind: 'quota' },
    { key: 'heavy_patching', label: 'Heavy Patching (m²)',    kind: 'quota' },
    { key: 'coldmix',        label: 'Cold Asphalt (m²)',      kind: 'quota' },
    { key: 'total',          label: 'Siling Bulanan (m²)',    kind: 'ceiling' },
    { key: null,             label: 'Peratusan Penggunaan (%)', kind: 'pct' },
  ],
  R02: [
    { key: 'protocolPrimary', label: 'Protokol/Utama (km)',    kind: 'class' },
    { key: 'secondaryMinor',  label: 'Sekunder/Minor (km)',    kind: 'class' },
    { key: 'total',           label: 'Penggunaan (km)',        kind: 'ceiling' },
    { key: null,              label: 'Peratusan Penggunaan (%)', kind: 'pct' },
  ],
  R06: [
    { key: 'protocolPrimary', label: 'Protokol/Utama (km)',    kind: 'class' },
    { key: 'secondaryMinor',  label: 'Sekunder/Minor (km)',    kind: 'class' },
    { key: 'total',           label: 'Siling Bulanan (m²)',    kind: 'ceiling' },
    { key: null,              label: 'Peratusan Penggunaan (%)', kind: 'pct' },
  ],
}

// Maps a `class` row's key to which districtLengths pair its per-class
// contract length comes from. Both R02 and R06 use these same two keys.
const CLASS_LENGTH_KEY = { protocolPrimary: 'pu', secondaryMinor: 'sm' }

// Normalise one endpoint bucket into the groups the table renders.
//
// `unattributed` (R01 cases with no mix_type, R02/R06 cases with no road class)
// is reported separately and deliberately excluded from `total`, so Siling
// Bulanan keeps equalling the sum of the rows above it as it does in the
// workbook. `unmeasured` is a different thing: cases that yielded no usable
// quantity at all. Neither is ever silently folded in as zero.
export function bucketUsage(scope, bucket, unit) {
  const scale = UNIT_SCALE[unit] != null ? UNIT_SCALE[unit] : 1
  const source = scope === 'R01' ? bucket.byMixType : bucket.byRoadClass
  const keys = USAGE_ROWS[scope].filter((r) => r.key && r.key !== 'total').map((r) => r.key)
  const groups = {}
  let total = 0

  keys.forEach((key) => {
    const q = ((source && source[key] && source[key].quantity) || 0) * scale
    groups[key] = q
    total += q
  })

  const count = bucket.count || 0
  const unmeasured = bucket.unmeasured || 0

  return {
    count,
    groups,
    total,
    unmeasured,
    unattributed: (source && source.unknown && source.unknown.count) || 0,
    allUnmeasured: count > 0 && unmeasured === count,
  }
}

const MONTH_MY = ['JANUARI', 'FEBRUARI', 'MAC', 'APRIL', 'MEI', 'JUN',
                  'JULAI', 'OGOS', 'SEPTEMBER', 'OKTOBER', 'NOVEMBER', 'DISEMBER']

function emptyCell() {
  return { value: 0, unmeasured: false }
}

// Build every figure the KJP tables and charts need, for one scope.
//
// `buckets` is the endpoint payload; `districts` carries the contract lengths.
// Districts with no bucket for a month still get a cell, so the table never has
// holes the view has to fill.
export function buildKjpModel({
  scope, districts, buckets, unit, year, startMonth, monthCount, asAtMonth,
}) {
  const months = calendarMonths(year)
  const elapsed = elapsedMonths(startMonth, asAtMonth, monthCount)
  const lastContractMonth = startMonth + monthCount - 1
  const rowDefs = USAGE_ROWS[scope]
  const warnings = []

  const byDistrictMonth = {}
  ;(buckets || []).forEach((b) => {
    byDistrictMonth[`${b.district}:${b.month}`] = b
  })

  const rows = (districts || []).map((district) => {
    const lengths = districtLengths(district)
    const annual = annualQuota(scope, lengths)
    const ceiling = monthlyCeiling(annual, monthCount)
    const split = quotaSplit(scope, annual)
    const contractKm = (lengths.pu + lengths.sm) / 1000

    if (!contractKm) warnings.push({ code: 'NO_CONTRACT_LENGTH', district: district.name, scope })

    const usage = months.map((m) => {
      const b = byDistrictMonth[`${district._id}:${m.index}`]

      return b ? bucketUsage(scope, b, unit) : null
    })

    if (usage.some((u) => u && u.allUnmeasured) && !usage.some((u) => u && u.total > 0)) {
      warnings.push({ code: 'NO_MEASUREMENTS', district: district.name, scope })
    }

    // Percentage rows are derived from the ceiling row afterwards, never by
    // summing or averaging percentages — that is how the district rows and the
    // totals row stay in agreement.
    const quantityRows = rowDefs.filter((def) => def.kind !== 'pct').map((def) => {
      const cells = usage.map((u) => {
        if (!u) return emptyCell()

        if (def.kind === 'count') return { value: u.count, unmeasured: false }

        const value = def.key === 'total' ? u.total : u.groups[def.key] || 0

        return { value, unmeasured: u.allUnmeasured }
      })

      const sum = (from, to) => cells
        .filter((_, i) => months[i].index >= from && months[i].index <= to)
        .reduce((acc, c) => acc + c.value, 0)

      // A range reads as unmeasured only when it has no real number to show:
      // at least one contributing cell was flagged unmeasured, and none of
      // them landed a positive value. A partly-measured range still shows its
      // sum — that total is real, just incomplete.
      const rangeUnmeasured = (from, to) => {
        const inRange = cells.filter((_, i) => months[i].index >= from && months[i].index <= to)

        return inRange.some((c) => c.unmeasured) && !inRange.some((c) => c.value > 0)
      }

      // "Sehingga" covers contract months up to the as-at month, but never
      // past the contract's own last month — an as-at date after the
      // contract ended must not pull in months the contract never covered.
      // "Tahunan" covers the whole calendar year, which is why work done
      // before the contract started still counts against the annual quota.
      //
      // Computed once and reused by both usedToDate and unmeasuredToDate
      // below (rather than each clamping asAtMonth separately) so the two
      // can never again disagree about which months "sehingga" covers — that
      // drift is exactly what let SEHINGGA show 0.00 and BAKI show the full
      // allowance unused for an as-at date past the contract's own end.
      const toDateEnd = Math.min(asAtMonth, lastContractMonth)
      const usedToDate = sum(startMonth, toDateEnd)
      const usedAnnual = sum(1, 12)

      let quota = null
      if (def.kind === 'ceiling') quota = ceiling
      else if (def.kind === 'quota' && split) quota = monthCount ? split[def.key] / monthCount : 0
      // R02 and R06's Protokol/Utama and Sekunder/Minor rows show that class's
      // own contract length in km, not a share of the annual quota — the
      // workbook puts a real figure there (e.g. Gombak 64.605 / 47.101), not
      // a blank. lengths.pu/sm are stored in metres (districtLengths), so /
      // 1000 is the same km conversion contractKm above already uses.
      else if (def.kind === 'class') quota = lengths[CLASS_LENGTH_KEY[def.key]] / 1000

      return {
        label: def.label, kind: def.kind, quota, cells,
        usedToDate,
        bakiToDate: def.kind === 'ceiling' ? ceiling * elapsed - usedToDate : null,
        usedAnnual,
        bakiAnnual: def.kind === 'ceiling' ? annual - usedAnnual : null,
        unmeasuredToDate: rangeUnmeasured(startMonth, toDateEnd),
        unmeasuredAnnual: rangeUnmeasured(1, 12),
      }
    })

    const subRows = quantityRows.concat(
      pctRow(rowDefs, quantityRows, ceiling, elapsed, annual)
    )

    return { districtId: district._id, districtName: district.name, contractKm, subRows }
  })

  const totals = buildTotals(rows, rowDefs, months, startMonth, asAtMonth, elapsed)
  const projections = buildProjections(rows, months, startMonth, lastContractMonth)

  return { scope, months, asAtLabel: MONTH_MY[asAtMonth - 1], rows, totals, projections, warnings }
}

// Every percentage in the sheet is usage over ceiling, so it is derived from
// the ceiling row rather than aggregated from other percentages.
function pctRow(rowDefs, quantityRows, ceiling, elapsed, annual) {
  const def = rowDefs.find((d) => d.kind === 'pct')
  const base = quantityRows.find((r) => r.kind === 'ceiling')
  const denomToDate = ceiling * elapsed
  const pct = (v, d) => (d ? (v / d) * 100 : 0)

  return {
    label: def.label,
    kind: 'pct',
    quota: null,
    cells: base.cells.map((c) => ({ value: pct(c.value, ceiling), unmeasured: c.unmeasured })),
    usedToDate: pct(base.usedToDate, denomToDate),
    bakiToDate: pct(base.bakiToDate, denomToDate),
    usedAnnual: pct(base.usedAnnual, annual),
    bakiAnnual: pct(base.bakiAnnual, annual),
    // The percentage row is derived from the ceiling row, so its unmeasured
    // state is too — a percentage computed from an unmeasured total is no
    // more real than the total itself.
    unmeasuredToDate: base.unmeasuredToDate,
    unmeasuredAnnual: base.unmeasuredAnnual,
  }
}

// The one "unmeasured" rule for the whole module: real when at least one
// contributor was flagged unmeasured AND none of them landed a positive
// value. Must stay identical to rangeUnmeasured (in buildKjpModel) and
// barsUnmeasured (in buildProjections) — a rolled-up total is not "every
// contributor unmeasured", it is "no real number to show", exactly like
// every other cell. Using `every` here let a single fully-idle district
// (or a district with no bucket at all) silently keep the grand total at
// "0.00" while every row above it correctly showed "—".
function someUnmeasuredNonePositive(items, unmeasuredOf, valueOf) {
  return items.some((i) => unmeasuredOf(i)) && !items.some((i) => valueOf(i) > 0)
}

// BAKI (remaining quota) only exists on ceiling rows; every other kind of
// sub-row carries `null` there for every district, on purpose. Summing with
// `|| 0` would turn "every contributor is null" into a bare 0 in the totals
// row while every district row above it correctly renders blank — the same
// family of defect as the four Criticals already fixed on this branch. Null
// wins only when *every* contributor is null; a genuine mix still sums.
function sumBakiOrNull(rows, defIndex, field) {
  if (rows.every((r) => r.subRows[defIndex][field] == null)) return null

  return rows.reduce((acc, r) => acc + (r.subRows[defIndex][field] || 0), 0)
}

function buildTotals(rows, rowDefs, months, startMonth, asAtMonth, elapsed) {
  const quantityDefs = rowDefs.filter((d) => d.kind !== 'pct')

  const quantityRows = quantityDefs.map((def, defIndex) => ({
    label: def.label,
    kind: def.kind,
    quota: rows.reduce((acc, r) => acc + (r.subRows[defIndex].quota || 0), 0) || null,
    cells: months.map((_, monthIndex) => ({
      value: rows.reduce((acc, r) => acc + r.subRows[defIndex].cells[monthIndex].value, 0),
      unmeasured: someUnmeasuredNonePositive(
        rows,
        (r) => r.subRows[defIndex].cells[monthIndex].unmeasured,
        (r) => r.subRows[defIndex].cells[monthIndex].value
      ),
    })),
    usedToDate: rows.reduce((acc, r) => acc + (r.subRows[defIndex].usedToDate || 0), 0),
    bakiToDate: sumBakiOrNull(rows, defIndex, 'bakiToDate'),
    usedAnnual: rows.reduce((acc, r) => acc + (r.subRows[defIndex].usedAnnual || 0), 0),
    bakiAnnual: sumBakiOrNull(rows, defIndex, 'bakiAnnual'),
    unmeasuredToDate: someUnmeasuredNonePositive(
      rows,
      (r) => r.subRows[defIndex].unmeasuredToDate,
      (r) => r.subRows[defIndex].usedToDate
    ),
    unmeasuredAnnual: someUnmeasuredNonePositive(
      rows,
      (r) => r.subRows[defIndex].unmeasuredAnnual,
      (r) => r.subRows[defIndex].usedAnnual
    ),
  }))

  const totalCeiling = quantityRows.find((r) => r.kind === 'ceiling')

  // Annual quota across the shown districts. Recovered from the ceiling row as
  // used + remaining, so it needs no second pass over the district lengths.
  const totalAnnualQuota = rows.reduce((acc, r) => {
    const c = r.subRows.find((s) => s.kind === 'ceiling')

    return acc + (c ? c.usedAnnual + (c.bakiAnnual || 0) : 0)
  }, 0)

  return quantityRows.concat(
    pctRow(rowDefs, quantityRows, (totalCeiling && totalCeiling.quota) || 0, elapsed, totalAnnualQuota)
  )
}

// The workbook's three bottom rows, which are also the chart's line series.
function buildProjections(rows, months, startMonth, lastContractMonth) {
  const allocation = rows.reduce((acc, r) => {
    const ceiling = r.subRows.find((s) => s.kind === 'ceiling')

    return acc + (ceiling ? ceiling.quota || 0 : 0)
  }, 0)

  const usageByMonth = months.map((_, i) => rows.reduce((acc, r) => {
    const ceiling = r.subRows.find((s) => s.kind === 'ceiling')

    return acc + (ceiling ? ceiling.cells[i].value : 0)
  }, 0))

  // Projection rows need the same unmeasured guard as everything else: for a
  // scope with no recorded dimensions anywhere (R06 today) these would
  // otherwise export a flat line of real-looking zeros.
  const ceilingCell = (r, i) => {
    const c = r.subRows.find((s) => s.kind === 'ceiling')

    return c ? c.cells[i] : null
  }
  const barsUnmeasured = months.map((_, i) =>
    rows.length > 0 &&
    rows.some((r) => { const c = ceilingCell(r, i); return c && c.unmeasured }) &&
    !rows.some((r) => { const c = ceilingCell(r, i); return c && c.value > 0 })
  )

  const cumulativeUsage = []
  let running = 0
  usageByMonth.forEach((v, i) => {
    running += v
    // Hold the last known value forward once data runs out, as the sheet does.
    // A line dropping to zero after the last reported month would read as
    // usage collapsing rather than as no data yet.
    cumulativeUsage[i] = i > 0 && v === 0 ? cumulativeUsage[i - 1] : running
  })

  // Accumulates over contract months only, so it lands exactly on the annual
  // quota at the last contract month instead of overshooting it. For a Mar-Dec
  // contract the line is flat at zero through Jan and Feb.
  const annualCeiling = months.map((m) => {
    const elapsedHere = Math.max(0, Math.min(m.index, lastContractMonth) - startMonth + 1)

    return allocation * elapsedHere
  })

  const cumulativeUsageUnmeasured = cumulativeUsage.map(
    (v, i) => v === 0 && barsUnmeasured.slice(0, i + 1).some(Boolean)
  )

  return {
    monthlyAllocation: months.map(() => allocation),
    annualCeiling,
    cumulativeUsage,
    cumulativeUsageUnmeasured,
    bars: usageByMonth,
    barsUnmeasured,
  }
}
